import TableHeader from '@/components/table/tableHeader';
import { useResponsive } from '@/hooks/use-responsive';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { InvestorDetailsLegalEntityAssetClassTargetsQuery, PaginateSortDirection } from '@/__generated__/graphql';
import { Toggle } from '@/components/basicComponents/toggle';
import {
  INVESTOR_DETAILS_COMMITMENT_PLANS_TOTAL_QUERY,
  INVESTOR_DETAILS_GROUP_ASSET_CLASS_TARGETS_QUERY,
  INVESTOR_DETAILS_LEGAL_ENTITY_ASSET_CLASS_TARGETS_QUERY
} from '@/components/investors-page/investorDetailsPage/queries';
import { InvestorDetailsEntityTypeEnum } from '@/components/investors-page/investorDetailsPage/types';
import { Loader } from '@/components/loaders/loader';
import { StickyLoaderWrapper } from '@/components/loaders/stickyTableLoader';
import { TableBodyWrapperWithLoading, TableWrapper } from '@/components/table/styles';
import { SORT_ORDER, TableColumns } from '@/components/table/types';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { useStatusData } from '@/hooks/useStatusData';
import useStickyFooter from '@/hooks/useStickyFooter';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import {
  overCommitmentRatioModeSummaryTableColumns,
  pacingModelModeSimplifiedSummaryTableColumns,
  pacingModelModeSummaryTableColumns
} from '../../constants';
import MobileTable from './mobileTable';
import SummaryTableBody from './tableBody';
import SummaryTableSumFooter from './tableSumFooter';

interface ISummaryTableProps {
  entityType: InvestorDetailsEntityTypeEnum;
}

const SummaryTable = ({ entityType }: ISummaryTableProps) => {
  const params = useParams();
  const { data: statusData } = useStatusData();
  const { isMobile, isTablet } = useResponsive();
  const { searchParams, onChangeSort, onChangeToggle } = useSearchParamsManager();

  const entityId = params?.entityId || '';
  const toggle = searchParams.get('toggle') === null || searchParams.get('toggle') === 'true';
  const key = searchParams.get('key') || 'name';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';

  const [tableColumns, setTableColumns] = useState<TableColumns[]>([]);
  const [tableRows, setTableRows] = useState<InvestorDetailsLegalEntityAssetClassTargetsQuery['legalEntity']['assetClassTargets']>([]);
  const [commitmentPlansTotal, setCommitmentPlansTotal] = useState<number | null>(null);
  const [ignoreAllocationTargets, setIgnoreAllocationTargets] = useState(false);
  const [targetOCRatio, setTargetOCRatio] = useState<number | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const containerRef = useRef<HTMLDivElement | null>(null);
  const footerRef = useRef<HTMLDivElement | null>(null);

  useStickyFooter(containerRef, footerRef);

  const { loading: legalEntityAssetClassTargetsLoading } = useQuery(INVESTOR_DETAILS_LEGAL_ENTITY_ASSET_CLASS_TARGETS_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !entityId || !entityType || entityType === InvestorDetailsEntityTypeEnum.Group,
    variables: {
      id: entityId,
      assetClassTargetsInput: {
        includingProposed: toggle,
        sortBy: key,
        sortDirection: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc
      }
    },
    onCompleted: (data) => {
      const { targetOCRatio, ignoreAllocationTargets, assetClassTargets } = data.legalEntity;
      setTargetOCRatio(targetOCRatio);
      setIgnoreAllocationTargets(ignoreAllocationTargets);
      setTableRows(assetClassTargets || []);
    }
  });

  const { loading: groupAssetClassTargetsLoading } = useQuery(INVESTOR_DETAILS_GROUP_ASSET_CLASS_TARGETS_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !entityId || !entityType || entityType !== InvestorDetailsEntityTypeEnum.Group,
    variables: {
      data: {
        primaryEntityId: entityId,
        includeRecommendations: toggle
      },
      assetClassTargetsInput: {
        includingProposed: toggle,
        sortBy: key,
        sortDirection: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc
      }
    },
    onCompleted: (data) => {
      const { targetOCRatio, ignoreAllocationTargets, assetClassTargets } = data.group;
      setTargetOCRatio(targetOCRatio);
      setIgnoreAllocationTargets(ignoreAllocationTargets);
      setTableRows(assetClassTargets || []);
    }
  });

  const { loading: commitmentPlansTotalLoading } = useQuery<{ commitmentPlans: { total: number } }>(INVESTOR_DETAILS_COMMITMENT_PLANS_TOTAL_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !entityId,
    variables: {
      input: {
        legalEntityId: entityId,
        sortBy: 'NAME',
        sortDirection: SORT_ORDER.ASC,
        limit: 1
      }
    },
    onCompleted: (data) => {
      setCommitmentPlansTotal(data.commitmentPlans.total);
    }
  });

  useEffect(() => {
    // if (ignoreAllocationTargets && targetOCRatio !== null) {
    //   setTableColumns(overCommitmentRatioModeSimplifiedSummaryTableColumns);
    //   return;
    // }
    if (ignoreAllocationTargets || entityType === InvestorDetailsEntityTypeEnum.GroupedEntity) {
      setTableColumns(pacingModelModeSimplifiedSummaryTableColumns);
      return;
    }
    if (targetOCRatio === null) {
      setTableColumns(pacingModelModeSummaryTableColumns);
      return;
    }
    setTableColumns(overCommitmentRatioModeSummaryTableColumns);
  }, [ignoreAllocationTargets, commitmentPlansTotal, targetOCRatio, entityType]);

  const isLoading = legalEntityAssetClassTargetsLoading || groupAssetClassTargetsLoading || commitmentPlansTotalLoading;

  return (
    <>
      <ToggleWrapper isMobile={isMobile} isTablet={isTablet}>
        <Toggle title="Include Pending Commitments" isActive={toggle} onToggle={() => onChangeToggle(toggle, { shouldSaveScrollPosition: false })} />
      </ToggleWrapper>
      {isMobile ? (
        <>
          {isLoading ? (
            <Loader />
          ) : (
            <MobileTable
              investorSummaryRows={tableRows}
              ignoreAllocationTargets={ignoreAllocationTargets}
              commitmentPlansTotal={commitmentPlansTotal}
              targetOCRatio={targetOCRatio}
              targetSubAssetClasses={statusData?.target_sub_asset_classes || false}
              entityType={entityType}
            />
          )}
        </>
      ) : (
        <TableWrapper padding="0">
          <CustomTableHeader isTablet={isTablet} refetch={() => {}} savedSort={{ key, asc }} savedSetSort={onChangeSort} columns={tableColumns} />
          {isLoading && (
            <StickyLoaderWrapper top={isTablet ? '190px' : '140px'}>
              <Loader />
            </StickyLoaderWrapper>
          )}
          <TableBodyWrapperWithLoading isLoading={isLoading}>
            <SummaryTableBody
              investorSummaryRows={tableRows}
              ignoreAllocationTargets={ignoreAllocationTargets}
              targetSubAssetClasses={statusData?.target_sub_asset_classes || false}
              commitmentPlansTotal={commitmentPlansTotal}
              targetOCRatio={targetOCRatio}
              entityType={entityType}
              expandedRows={expandedRows}
              setExpandedRows={setExpandedRows}
            />
            <TableSumFooterWrapper ref={footerRef}>
              <SummaryTableSumFooter
                investorSummaryRows={tableRows}
                ignoreAllocationTargets={ignoreAllocationTargets}
                commitmentPlansTotal={commitmentPlansTotal}
                targetOCRatio={targetOCRatio}
                targetSubAssetClasses={statusData?.target_sub_asset_classes || false}
                entityType={entityType}
              />
            </TableSumFooterWrapper>
            <div ref={containerRef}></div>
          </TableBodyWrapperWithLoading>
        </TableWrapper>
      )}
    </>
  );
};

export default SummaryTable;

const ToggleWrapper = styled.div<{ isMobile: boolean; isTablet: boolean }>`
  display: flex;
  justify-content: flex-end;
  padding: 30px 10px 20px 0;
  position: sticky;
  top: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '50px' : '0')};
  z-index: 9;
  background-color: ${themeVar('layer.base')};
`;

const CustomTableHeader = styled(TableHeader)<{ isTablet: boolean }>`
  top: ${({ isTablet }) => (isTablet ? '116px' : '66px')};
`;

const TableSumFooterWrapper = styled.div`
  &.sticky {
    position: sticky;
    bottom: 0;
    z-index: 1;
  }
`;
